export type QueryParamValue =
  | string
  | string[]
  | number
  | boolean
  | undefined
  | null;

export interface RouteDefinition<Path extends string> {
  readonly path: Path;
  readonly method: 'GET' | 'POST';
  readonly requiresAuth: boolean;
  url(queryParams?: Record<string, QueryParamValue>): string;
}

function defineRoute<Path extends string>(
  path: Path,
  method: RouteDefinition<Path>['method'],
  requiresAuth: boolean,
): RouteDefinition<Path> {
  return {
    path,
    method,
    requiresAuth,
    url(queryParams?: Record<string, QueryParamValue>): string {
      if (!queryParams) return path;
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(queryParams)) {
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) {
          v.forEach((item) => params.append(k, item));
        } else {
          params.append(k, String(v));
        }
      }
      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },
  };
}

export const paths = {
  // cards
  addUrlToLibrary: '/network.cosmik.card.addUrl',
  addCardToLibrary: '/network.cosmik.card.addToLibrary',
  addCardToCollection: '/network.cosmik.card.addToCollection',
  urlCardAssociations: '/network.cosmik.card.updateUrlAssociations',
  myUrlCards: '/network.cosmik.card.listMine',
  urlMetadata: '/network.cosmik.card.getUrlMetadata',
  urlLibraryStatus: '/network.cosmik.card.getLibraryStatus',
  librariesForUrl: '/network.cosmik.card.getLibrariesForUrl',
  noteCardsForUrl: '/network.cosmik.card.getNoteCardsForUrl',
  searchCards: '/network.cosmik.card.search',
  cardById: '/network.cosmik.card.get',
  cardLibraries: '/network.cosmik.card.getLibraries',
  cardNote: '/network.cosmik.card.updateNote',
  removeFromLibrary: '/network.cosmik.card.removeFromLibrary',
  removeFromCollections: '/network.cosmik.card.removeFromCollections',
  cardsByUser: '/network.cosmik.card.listByUser',
  // collections
  myCollections: '/network.cosmik.collection.listMine',
  createCollection: '/network.cosmik.collection.create',
  collectionsForUrl: '/network.cosmik.collection.getForUrl',
  searchCollections: '/network.cosmik.collection.search',
  collectionById: '/network.cosmik.collection.get',
  updateCollection: '/network.cosmik.collection.update',
  deleteCollection: '/network.cosmik.collection.delete',
  collectionsByUser: '/network.cosmik.collection.listByUser',
  collectionByAtUri: '/network.cosmik.collection.getByAtUri',
  openWithContributor: '/network.cosmik.collection.listContributed',
  collectionFollowers: '/network.cosmik.collection.getFollowers',
  collectionFollowersCount: '/network.cosmik.collection.getFollowerCount',
  collectionContributors: '/network.cosmik.collection.getContributors',
  recommendedCollectionsForUrl:
    '/network.cosmik.collection.getRecommendedForUrl',
  // users / actor
  myProfile: '/network.cosmik.actor.getMyProfile',
  userProfile: '/network.cosmik.actor.getProfile',
  getOnboardingState: '/network.cosmik.actor.getOnboardingState',
  updateOnboardingState: '/network.cosmik.actor.updateOnboardingState',
  initiateOAuth: '/network.cosmik.server.initiateOAuth',
  oauthCallback: '/network.cosmik.server.oauthCallback',
  loginWithAppPassword: '/network.cosmik.server.createSession',
  refreshToken: '/network.cosmik.server.refreshSession',
  logout: '/network.cosmik.server.deleteSession',
  extensionTokens: '/network.cosmik.server.getExtensionTokens',
  followTarget: '/network.cosmik.graph.follow',
  unfollowTarget: '/network.cosmik.graph.unfollow',
  followingUsers: '/network.cosmik.graph.getFollowing',
  bskyFollowedUsers: '/network.cosmik.graph.getBskyFollowedUsers',
  followMany: '/network.cosmik.graph.followMany',
  userFollowers: '/network.cosmik.graph.getFollowers',
  followingCollections: '/network.cosmik.graph.getFollowingCollections',
  followingCount: '/network.cosmik.graph.getFollowingCount',
  userFollowersCount: '/network.cosmik.graph.getFollowersCount',
  followingCollectionsCount:
    '/network.cosmik.graph.getFollowingCollectionsCount',
  // api keys
  listApiKeys: '/network.cosmik.apiKey.list',
  createApiKey: '/network.cosmik.apiKey.create',
  updateApiKey: '/network.cosmik.apiKey.update',
  revokeApiKey: '/network.cosmik.apiKey.revoke',
  // feeds
  globalFeed: '/network.cosmik.feed.getGlobal',
  gemFeed: '/network.cosmik.feed.getGem',
  followingFeed: '/network.cosmik.feed.getFollowing',
  bskyFollowingFeed: '/network.cosmik.feed.getBskyFollowing',
  // notifications
  myNotifications: '/network.cosmik.notification.list',
  unreadCount: '/network.cosmik.notification.getUnreadCount',
  markRead: '/network.cosmik.notification.markRead',
  markAllRead: '/network.cosmik.notification.markAllRead',
  // connections
  connectionsForUrl: '/network.cosmik.connection.getForUrl',
  createConnection: '/network.cosmik.connection.create',
  connectionsByUser: '/network.cosmik.connection.listByUser',
  updateConnection: '/network.cosmik.connection.update',
  deleteConnection: '/network.cosmik.connection.delete',
  // search
  similarUrls: '/network.cosmik.search.getSimilarUrls',
  semantic: '/network.cosmik.search.semantic',
  bskyPosts: '/network.cosmik.search.getBskyPosts',
  atProtoAccounts: '/network.cosmik.search.getAccounts',
  leafletDocs: '/network.cosmik.search.getLeafletDocs',
  recommended: '/network.cosmik.search.getRecommended',
  recommendedUsers: '/network.cosmik.search.getRecommendedUsers',
  recommendedCollections: '/network.cosmik.search.getRecommendedCollections',
  // graph (graphView namespace to avoid collision with social graph)
  graphData: '/network.cosmik.graphView.getData',
  userGraphData: '/network.cosmik.graphView.getUserData',
  urlGraphData: '/network.cosmik.graphView.getUrlData',
  // subscriptions
  subscribeToTarget: '/network.cosmik.graph.subscribe',
  unsubscribeFromTarget: '/network.cosmik.graph.unsubscribe',
  updateSubscription: '/network.cosmik.graph.updateSubscription',
  getMySubscriptions: '/network.cosmik.graph.getSubscriptions',
} as const;

export const routes = {
  cards: {
    addUrlToLibrary: defineRoute(paths.addUrlToLibrary, 'POST', true),
    addCardToLibrary: defineRoute(paths.addCardToLibrary, 'POST', true),
    addCardToCollection: defineRoute(paths.addCardToCollection, 'POST', true),
    urlCardAssociations: defineRoute(paths.urlCardAssociations, 'POST', true),
    myUrlCards: defineRoute(paths.myUrlCards, 'GET', true),
    urlMetadata: defineRoute(paths.urlMetadata, 'GET', false),
    urlLibraryStatus: defineRoute(paths.urlLibraryStatus, 'GET', true),
    librariesForUrl: defineRoute(paths.librariesForUrl, 'GET', false),
    noteCardsForUrl: defineRoute(paths.noteCardsForUrl, 'GET', false),
    searchCards: defineRoute(paths.searchCards, 'GET', false),
    cardById: defineRoute(paths.cardById, 'GET', false),
    cardLibraries: defineRoute(paths.cardLibraries, 'GET', false),
    cardNote: defineRoute(paths.cardNote, 'POST', true),
    removeFromLibrary: defineRoute(paths.removeFromLibrary, 'POST', true),
    removeFromCollections: defineRoute(
      paths.removeFromCollections,
      'POST',
      true,
    ),
    cardsByUser: defineRoute(paths.cardsByUser, 'GET', false),
  },
  collections: {
    myCollections: defineRoute(paths.myCollections, 'GET', true),
    createCollection: defineRoute(paths.createCollection, 'POST', true),
    collectionsForUrl: defineRoute(paths.collectionsForUrl, 'GET', false),
    searchCollections: defineRoute(paths.searchCollections, 'GET', false),
    collectionById: defineRoute(paths.collectionById, 'GET', false),
    updateCollection: defineRoute(paths.updateCollection, 'POST', true),
    deleteCollection: defineRoute(paths.deleteCollection, 'POST', true),
    collectionsByUser: defineRoute(paths.collectionsByUser, 'GET', false),
    collectionByAtUri: defineRoute(paths.collectionByAtUri, 'GET', false),
    openWithContributor: defineRoute(paths.openWithContributor, 'GET', false),
    followers: defineRoute(paths.collectionFollowers, 'GET', false),
    followersCount: defineRoute(paths.collectionFollowersCount, 'GET', false),
    contributors: defineRoute(paths.collectionContributors, 'GET', false),
    recommendedForUrl: defineRoute(
      paths.recommendedCollectionsForUrl,
      'GET',
      true,
    ),
  },
  users: {
    myProfile: defineRoute(paths.myProfile, 'GET', true),
    userProfile: defineRoute(paths.userProfile, 'GET', false),
    initiateOAuth: defineRoute(paths.initiateOAuth, 'GET', false),
    oauthCallback: defineRoute(paths.oauthCallback, 'GET', false),
    loginWithAppPassword: defineRoute(
      paths.loginWithAppPassword,
      'POST',
      false,
    ),
    refreshToken: defineRoute(paths.refreshToken, 'POST', false),
    logout: defineRoute(paths.logout, 'POST', true),
    extensionTokens: defineRoute(paths.extensionTokens, 'GET', true),
    getOnboardingState: defineRoute(paths.getOnboardingState, 'GET', true),
    updateOnboardingState: defineRoute(
      paths.updateOnboardingState,
      'POST',
      true,
    ),
  },
  apiKeys: {
    listApiKeys: defineRoute(paths.listApiKeys, 'GET', true),
    createApiKey: defineRoute(paths.createApiKey, 'POST', true),
    updateApiKey: defineRoute(paths.updateApiKey, 'POST', true),
    revokeApiKey: defineRoute(paths.revokeApiKey, 'POST', true),
  },
  feeds: {
    global: defineRoute(paths.globalFeed, 'GET', false),
    gem: defineRoute(paths.gemFeed, 'GET', false),
    following: defineRoute(paths.followingFeed, 'GET', true),
    bskyFollowing: defineRoute(paths.bskyFollowingFeed, 'GET', true),
  },
  notifications: {
    myNotifications: defineRoute(paths.myNotifications, 'GET', true),
    unreadCount: defineRoute(paths.unreadCount, 'GET', true),
    markRead: defineRoute(paths.markRead, 'POST', true),
    markAllRead: defineRoute(paths.markAllRead, 'POST', true),
  },
  connections: {
    connectionsForUrl: defineRoute(paths.connectionsForUrl, 'GET', false),
    createConnection: defineRoute(paths.createConnection, 'POST', true),
    connectionsByUser: defineRoute(paths.connectionsByUser, 'GET', false),
    updateConnection: defineRoute(paths.updateConnection, 'POST', true),
    deleteConnection: defineRoute(paths.deleteConnection, 'POST', true),
  },
  search: {
    similarUrls: defineRoute(paths.similarUrls, 'GET', false),
    semantic: defineRoute(paths.semantic, 'GET', false),
    bskyPosts: defineRoute(paths.bskyPosts, 'GET', false),
    atProtoAccounts: defineRoute(paths.atProtoAccounts, 'GET', false),
    leafletDocs: defineRoute(paths.leafletDocs, 'GET', false),
    recommended: defineRoute(paths.recommended, 'GET', false),
    recommendedUsers: defineRoute(paths.recommendedUsers, 'GET', true),
    recommendedCollections: defineRoute(
      paths.recommendedCollections,
      'GET',
      true,
    ),
  },
  graph: {
    graphData: defineRoute(paths.graphData, 'GET', true),
    userGraphData: defineRoute(paths.userGraphData, 'GET', false),
    urlGraphData: defineRoute(paths.urlGraphData, 'GET', false),
    followTarget: defineRoute(paths.followTarget, 'POST', true),
    unfollowTarget: defineRoute(paths.unfollowTarget, 'POST', true),
    followingUsers: defineRoute(paths.followingUsers, 'GET', false),
    bskyFollowedUsers: defineRoute(paths.bskyFollowedUsers, 'GET', true),
    followMany: defineRoute(paths.followMany, 'POST', true),
    followers: defineRoute(paths.userFollowers, 'GET', false),
    followingCollections: defineRoute(paths.followingCollections, 'GET', false),
    followingCount: defineRoute(paths.followingCount, 'GET', false),
    followersCount: defineRoute(paths.userFollowersCount, 'GET', false),
    followingCollectionsCount: defineRoute(
      paths.followingCollectionsCount,
      'GET',
      false,
    ),
  },
  subscriptions: {
    subscribeToTarget: defineRoute(paths.subscribeToTarget, 'POST', true),
    unsubscribeFromTarget: defineRoute(
      paths.unsubscribeFromTarget,
      'POST',
      true,
    ),
    updateSubscription: defineRoute(paths.updateSubscription, 'POST', true),
    getMySubscriptions: defineRoute(paths.getMySubscriptions, 'GET', true),
  },
} as const;
